/**
 * fiscal.ts
 * Argentine VAT vocabulary shared by the company profile (issuer), the
 * supplier ficha (sumi) and purchase orders (compras).
 *
 * Two independent facts decide the VAT on a purchase order:
 *   - the RATE belongs to the good/service (item_master.iva_rate, frozen on
 *     po_lines.iva_rate when the order is emitted) — never to the supplier;
 *   - the supplier's TAX CONDITION decides whether VAT is itemised at all: a
 *     monotributista or an exempt supplier invoices without VAT, so the order
 *     shows the net total as final.
 *
 * Quoted and ordered prices are always net of VAT; VAT is added on top.
 */
import { z } from 'zod';

export const TAX_CONDITIONS = ['responsable_inscripto', 'monotributo', 'exento'] as const;
export const TaxConditionSchema = z.enum(TAX_CONDITIONS);
export type TaxCondition = z.infer<typeof TaxConditionSchema>;

export const TAX_CONDITION_LABELS: Record<TaxCondition, string> = {
  responsable_inscripto: 'IVA Responsable Inscripto',
  monotributo: 'Monotributo',
  exento: 'IVA Exento',
};

/** Only a responsable inscripto itemises VAT. An unknown condition (null) is treated as one — the common case. */
export function itemisesIva(taxCondition: TaxCondition | null): boolean {
  return taxCondition === null || taxCondition === 'responsable_inscripto';
}

/** Rates in force in Argentina, as percentages. 0 = exento / no gravado. */
export const IVA_RATES = [0, 2.5, 5, 10.5, 21, 27] as const;
export type IvaRate = (typeof IVA_RATES)[number];

/** The general rate — applies whenever an item has no rate of its own. */
export const DEFAULT_IVA_RATE: IvaRate = 21;

export const IvaRateSchema = z
  .number()
  .refine((value): value is IvaRate => (IVA_RATES as readonly number[]).includes(value), {
    message: `La alícuota de IVA debe ser una de: ${IVA_RATES.join(', ')}.`,
  });

export function ivaRateLabel(rate: number): string {
  return rate === 0 ? 'Exento' : `${String(rate).replace('.', ',')} %`;
}
